import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, ThemePreference, ThemeService } from '@app/core';
import { environment } from '@app/environment';
import { EMPTY, finalize } from 'rxjs';
import { AppMenuItem } from '../../util/menu-item';

type HeaderOption = {
  name: string;
  id: string;
  isLink?: boolean;
  isExternal?: boolean;
  icon?: string;
};

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HeaderComponent {
  private router = inject(Router);
  private auth = inject(AuthService);
  protected readonly theme = inject(ThemeService);

  /** The colour schemes of the user menu, as radio items. */
  protected readonly themeOptions: {
    value: ThemePreference;
    label: string;
    icon: string;
  }[] = [
    {
      value: 'light',
      label: 'common.theme.light',
      icon: 'app-icon fa-solid fa-sun',
    },
    {
      value: 'dark',
      label: 'common.theme.dark',
      icon: 'app-icon fa-solid fa-moon',
    },
    {
      value: 'system',
      label: 'common.theme.system',
      icon: 'app-icon fa-solid fa-circle-half-stroke',
    },
  ];

  /** Whether the side menu drawer is open, for the menu button on narrow screens. */
  @Input() menuOpened = false;
  @Output() toggleMenu = new EventEmitter<void>();

  /** The user menu. The backend pages are in the side menu. */
  public options: HeaderOption[] = [
    {
      name: 'common.nav.changePassword',
      id: '/change-password',
      icon: 'app-icon fa-solid fa-key',
    },
    {
      name: 'common.nav.feedback',
      id: 'https://spryfox.atlassian.net/servicedesk/customer/portal/6',
      isLink: true,
      isExternal: true,
      icon: 'app-icon fa-solid fa-comment',
    },
    {
      name: 'common.nav.signOut',
      id: '/login',
      icon: 'app-icon fa-solid fa-arrow-right-from-bracket',
    },
  ];
  /** The options as menu entries: external links open in a new tab. */
  public optionItems: AppMenuItem[] = this.options.map((option) =>
    option.isExternal
      ? {
          label: option.name,
          icon: option.icon,
          url: option.id,
          target: '_blank',
        }
      : {
          label: option.name,
          icon: option.icon,
          command: () => this.optionClicked({ itemData: option }),
        }
  );
  public showMobileSearch = false;
  public userName$ = this.auth.username$;
  /** Whether the editor of a new list is open ("New list"). */
  public editorActive = false;
  /**
   * The name of the app links home on narrow screens. The logo of the white
   * label build is empty, so the header shows the name, as the sidebar does.
   */
  public readonly appName = environment.branding.appName;

  optionClicked = (e: { itemData: HeaderOption }) => {
    (e.itemData.id === '/login' ? this.auth.logout() : EMPTY)
      .pipe(
        finalize(() => {
          if (!e.itemData.isExternal) {
            this.router.navigateByUrl(e.itemData.id);
          }
        })
      )
      .subscribe();
  };

  toggle = () => {
    this.toggleMenu.emit();
  };
}
